import { useState, useRef, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FaUser, FaMapMarkerAlt, FaBriefcase, FaDollarSign, FaCalendarAlt, FaFileAlt, FaChevronDown, FaChevronUp, FaCheckCircle } from 'react-icons/fa'
import { useProfileStatus, useProviderProfile } from '@/hooks/useProfile'
import { useServiceOfferings } from '@/hooks/useServices'
import PersonalInfoStep from '@/components/steps/PersonalInfoStep'
import LocationStep from '@/components/steps/LocationStep'
import ServicesStep from '@/components/steps/ServicesStep'
import ServicePricingStep from '@/components/steps/ServicePricingStep'
import AvailabilityStep from '@/components/steps/AvailabilityStep'
import CertificateStep from '@/components/steps/CertificateStep'
import './ProviderSetup.css'

const SECTION_ORDER = ['personal', 'location', 'services', 'pricing', 'availability', 'certificate']

const hasEnabledAvailability = (availabilitySchedule: any): boolean => {
    if (!availabilitySchedule || typeof availabilitySchedule !== 'object') return false
    return Object.values(availabilitySchedule).some((day: any) => {
        if (!day || typeof day !== 'object') return false
        const enabled = Boolean(day.enabled)
        const slots = Array.isArray(day.slots) ? day.slots : []
        return enabled && slots.length > 0
    })
}

const ProviderSetup = () => {
    const { data: status, isLoading: statusLoading, isError } = useProfileStatus()
    const { data: providerProfile, isLoading: providerLoading } = useProviderProfile()
    const { data: offeringsData, isLoading: offeringsLoading } = useServiceOfferings()
    const [searchParams] = useSearchParams()

    const [expandedSection, setExpandedSection] = useState<string | null>(null)
    const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([])
    const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
    const [initialStepApplied, setInitialStepApplied] = useState(false)

    useEffect(() => {
        if (Array.isArray(offeringsData)) {
            const ids = offeringsData
                .map((offering: any) => {
                    if (typeof offering.service === 'number') return offering.service
                    if (offering.service?.id) return offering.service.id
                    return undefined
                })
                .filter((id: number | undefined): id is number => typeof id === 'number')
            if (ids.length) {
                setSelectedServiceIds(ids)
            }
        }
    }, [offeringsData])

    const toggleSection = (id: string) => {
        setExpandedSection(expandedSection === id ? null : id)

        setTimeout(() => {
            const element = sectionRefs.current[id]
            if (element && expandedSection !== id) {
                const yOffset = -80
                const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset
                window.scrollTo({ top: y, behavior: 'smooth' })
            }
        }, 100)
    }

    const handleStepComplete = (currentStepId: string) => {
        const currentIndex = SECTION_ORDER.indexOf(currentStepId)

        setExpandedSection(null)

        setTimeout(() => {
            if (currentIndex >= 0 && currentIndex < SECTION_ORDER.length - 1) {
                const nextSectionId = SECTION_ORDER[currentIndex + 1]
                setExpandedSection(nextSectionId)

                setTimeout(() => {
                    const element = sectionRefs.current[nextSectionId]
                    if (element) {
                        const yOffset = -80
                        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset
                        window.scrollTo({ top: y, behavior: 'smooth' })
                    }
                }, 100)
            }
        }, 300)
    }

    const servicesComplete = selectedServiceIds.length > 0

    const pricingComplete = useMemo(() => {
        if (!Array.isArray(offeringsData) || selectedServiceIds.length === 0) return false
        const offeringsByService = new Map<number, any>()
        offeringsData.forEach((offering: any) => {
            const serviceId = typeof offering.service === 'number' ? offering.service : offering.service?.id
            if (typeof serviceId === 'number') {
                offeringsByService.set(serviceId, offering)
            }
        })
        const allSelectedConfigured = selectedServiceIds.every((serviceId) => offeringsByService.has(serviceId))
        if (!allSelectedConfigured) return false
        return Array.from(offeringsByService.values()).every((offering: any) => {
            if (offering.pricing_type === 'hourly') {
                return offering.hourly_rate && offering.minimum_hours
            }
            return true
        })
    }, [offeringsData, selectedServiceIds])

    const availabilityComplete = status?.has_availability ?? hasEnabledAvailability(providerProfile?.availability_schedule)
    const certificateComplete = status?.is_verified ?? Boolean(providerProfile?.is_verified)
    const districtsComplete = status?.has_districts ?? Boolean(providerProfile?.service_districts?.length)

    const sections = [
        {
            id: 'personal',
            title: 'Información Personal',
            desc: 'Completa tu información básica y sube tu foto de perfil',
            icon: FaUser,
            isComplete: Boolean(status?.is_profile_configured || (providerProfile?.profile?.first_name && providerProfile?.profile?.last_name)),
            render: () => <PersonalInfoStep onComplete={() => handleStepComplete('personal')} />,
        },
        {
            id: 'location',
            title: 'Cobertura',
            desc: 'Selecciona los distritos donde atiendes',
            icon: FaMapMarkerAlt,
            isComplete: districtsComplete,
            render: () => <LocationStep onComplete={() => handleStepComplete('location')} />,
        },
        {
            id: 'services',
            title: 'Servicios',
            desc: 'Activa los servicios que ofreces',
            icon: FaBriefcase,
            isComplete: servicesComplete,
            render: () => (
                <ServicesStep
                    initialSelected={selectedServiceIds}
                    onSelectionChange={setSelectedServiceIds}
                    onComplete={() => handleStepComplete('services')}
                />
            ),
        },
        {
            id: 'pricing',
            title: 'Precios y detalles',
            desc: 'Define tarifas, notas y requisitos por servicio',
            icon: FaDollarSign,
            isComplete: pricingComplete,
            render: () => (
                <ServicePricingStep
                    selectedServiceIds={selectedServiceIds}
                    onComplete={() => handleStepComplete('pricing')}
                />
            ),
        },
        {
            id: 'availability',
            title: 'Disponibilidad',
            desc: 'Configura tus horarios para servicios por hora',
            icon: FaCalendarAlt,
            isComplete: availabilityComplete,
            render: () => (
                <AvailabilityStep onComplete={() => handleStepComplete('availability')} />
            ),
        },
        {
            id: 'certificate',
            title: 'Certificado Laboral',
            desc: 'Sube tu Certificado Único Laboral para publicar servicios',
            icon: FaFileAlt,
            isComplete: certificateComplete,
            render: () => (
                <CertificateStep
                    isVerified={certificateComplete}
                    onComplete={() => handleStepComplete('certificate')}
                />
            ),
        },
    ]

    const isLoading = statusLoading || providerLoading || offeringsLoading

    const focusStep = searchParams.get('step')

    useEffect(() => {
        if (!focusStep || initialStepApplied || isLoading) {
            return
        }

        if (!SECTION_ORDER.includes(focusStep)) {
            return
        }

        setExpandedSection(focusStep)
        setInitialStepApplied(true)

        setTimeout(() => {
            const element = sectionRefs.current[focusStep]
            if (element) {
                const yOffset = -80
                const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset
                window.scrollTo({ top: y, behavior: 'smooth' })
            }
        }, 300)
    }, [focusStep, initialStepApplied, isLoading])

    return (
        <div className="provider-setup-page">
            <div className="provider-setup-header">
                <div className="provider-setup-header-content">
                    <h1 className="provider-setup-title">Configuración de Proveedor</h1>
                    <p className="provider-setup-subtitle">Completa todos los pasos para empezar a ofrecer servicios</p>
                </div>
            </div>

            <div className="provider-setup-container">
                {isLoading ? (
                    <div className="provider-setup-loading">Cargando...</div>
                ) : isError ? (
                    <div className="provider-setup-error">Error al cargar configuración</div>
                ) : (
                    <div className="provider-setup-sections">
                        {sections.map((section) => {
                            const IconComponent = section.icon
                            const isExpanded = expandedSection === section.id
                            return (
                                <div
                                    key={section.id}
                                    ref={(el) => {
                                        sectionRefs.current[section.id] = el
                                    }}
                                    className={`provider-setup-card ${isExpanded ? 'provider-setup-card--expanded' : ''}`}
                                >
                                    <div
                                        className="provider-setup-card-header"
                                        onClick={() => toggleSection(section.id)}
                                    >
                                        <div className="provider-setup-card-left">
                                            <div className="provider-setup-card-icon">
                                                <IconComponent />
                                            </div>
                                            <div className="provider-setup-card-info">
                                                <h3 className="provider-setup-card-title">{section.title}</h3>
                                                <p className="provider-setup-card-desc">{section.desc}</p>
                                            </div>
                                        </div>
                                        <div className="provider-setup-card-right">
                                            {section.isComplete && (
                                                <FaCheckCircle className="provider-setup-check" />
                                            )}
                                            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                        </div>
                                    </div>
                                    {isExpanded && (
                                        <div className="provider-setup-card-content">
                                            {section.render()}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}

export default ProviderSetup


